import { useState, useEffect, useCallback, useRef } from 'react';
import ProjectDetailPanel from './ProjectDetailPanel';
import { Loader2, X, ChevronDown, Plus, Pencil, Trash2, FolderKanban, Search, FileDown } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENCON_ORANGE = '#ee7203';

function truncate(str, max = 22) {
  if (!str) return '—';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

export default function ProjectTabView({
  list, user, activeTabIndex, onTabChange, onRemoveProject, onAddProjects, onProjectsDataChange,
  // toolbar props
  lists, activeListId, onSelectList, onNewList, onEditList, onDeleteList, activeList,
}) {
  const projectIds = list?.project_ids || [];
  const [projectsData, setProjectsData] = useState({});
  const onProjectsDataChangeRef = useRef(onProjectsDataChange);
  onProjectsDataChangeRef.current = onProjectsDataChange;

  // Toolbar state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const canManageLists = user?.role === 'super_admin' || user?.can_add_project_tracking_lists;
  const canAdd = true; // all users can add projects to an existing list
  const activeListObj = (lists || []).find(l => l.id === activeListId);

  // Propagate projectsData changes to parent (via ref to avoid stale closure)
  useEffect(() => {
    onProjectsDataChangeRef.current?.(projectsData);
  }, [projectsData]);

  const fetchProject = useCallback(async (id) => {
    if (projectsData[id]) return;
    setProjectsData(prev => ({ ...prev, [id]: { loading: true } }));
    try {
      const res = await api.get(`${API}/zoho/project/${id}`);
      setProjectsData(prev => ({ ...prev, [id]: { loading: false, project: res.data.project } }));
    } catch (e) {
      setProjectsData(prev => ({ ...prev, [id]: { loading: false, error: true } }));
    }
  }, [projectsData]); // eslint-disable-line

  // Fetch info for all projects in the list (in small batches to avoid rate limits)
  useEffect(() => {
    if (projectIds.length === 0) return;
    const unfetched = projectIds.filter(id => !projectsData[id]);
    if (unfetched.length === 0) return;
    // Load EVERY project's name for the dropdown (it used to stop after the first 5, so the rest showed "..." until clicked) —
    // three at a time with a short pause between, so the Zoho API limits are still respected.
    (async () => {
      for (let i = 0; i < unfetched.length; i += 3) {
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(unfetched.slice(i, i + 3).map((id) => fetchProject(id)));
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    })();
  }, [projectIds]); // eslint-disable-line

  // Fetch active project if not yet loaded
  useEffect(() => {
    const id = projectIds[activeTabIndex];
    if (id && !projectsData[id]) fetchProject(id);
  }, [activeTabIndex, projectIds]); // eslint-disable-line

  const handleExportPdf = async () => {
    if (!activeListObj) return;
    const pIds = activeList?.project_ids || [];
    if (pIds.length === 0) { toast.error('No projects in this list'); return; }
    setExporting(true);
    const toastId = toast.loading('Fetching project data…');
    const token = localStorage.getItem('token');
    const hdr = { Authorization: `Bearer ${token}` };

    try {
      const { jsPDF } = await import('jspdf');

      // ── Fetch everything per project ───────────────────────────────────
      const enriched = await Promise.all(pIds.map(async (id) => {
        const project = projectsData?.[id]?.project || { id, name: id };

        // Packages + project notes + project quotes in parallel
        const [pkgData, projNoteData, projQuoteData] = await Promise.all([
          fetch(`${API}/zoho/project/${id}/packages`, { headers: hdr }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
          fetch(`${API}/zoho/project/${id}/notes`,    { headers: hdr }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
          fetch(`${API}/zoho/project/${id}/quotes`,   { headers: hdr }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
        ]);

        const allPkgs = [
          ...(pkgData.contractor_packages     || []).map(p => ({ ...p, _role: 'Contractor',    _module: 'Contractor_Packages' })),
          ...(pkgData.sub_contractor_packages || []).map(p => ({ ...p, _role: 'Sub-Contractor', _module: 'Sub_Contractor_Packages' })),
        ];

        // Per-package notes + tasks + first contact in parallel
        const pkgsWithDetails = await Promise.all(allPkgs.map(async (pkg) => {
          const accountId = pkg._role === 'Sub-Contractor' ? pkg.sub_contractor_account_id : pkg.contractor_account_id;
          const [nd, td, contactData] = await Promise.all([
            fetch(`${API}/zoho/package/${pkg._module}/${pkg.id}/notes`, { headers: hdr }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
            fetch(`${API}/zoho/package/${pkg._module}/${pkg.id}/tasks`, { headers: hdr }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
            accountId ? fetch(`${API}/zoho/account/${accountId}/contacts`, { headers: hdr }).then(r => r.ok ? r.json() : {}).catch(() => ({})) : Promise.resolve({}),
          ]);
          const contacts = contactData.contacts || [];
          return { ...pkg, _notes: nd.notes || [], _tasks: td.tasks || [], _firstContact: contacts[0] || null };
        }));

        // Project-level quotes not linked to any specific package
        const projectQuotes = (projQuoteData.quotes || []).filter(q => !q.contractor_package_id && !q.sub_contractor_package_id);

        return { project, packages: pkgsWithDetails, projectNotes: projNoteData.notes || [], projectQuotes };
      }));

      // ── PDF setup ─────────────────────────────────────────────────────
      const doc  = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const PW   = doc.internal.pageSize.getWidth();
      const PH   = doc.internal.pageSize.getHeight();
      const M    = 12;
      const OG   = [238, 114, 3];
      const TODAY = new Date().toLocaleDateString('en-GB');
      const listName = activeListObj.name;

      const fmt = (v) => {
        if (!v && v !== 0) return '—';
        const n = parseFloat(v);
        return isNaN(n) || n === 0 ? String(v) : `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
      };
      const fmtDate = (d) => {
        if (!d) return '';
        try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
        catch { return ''; }
      };

      // ── PAGE 1: INDEX ─────────────────────────────────────────────────
      doc.setFillColor(...OG);
      doc.rect(0, 0, PW, 34, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18); doc.setFont('helvetica', 'bold');
      doc.text(listName, M, 14, { maxWidth: PW - M * 2 });
      doc.setFontSize(8); doc.setFont('helvetica', 'normal');
      doc.text(`Project Tracking Report  ·  ${TODAY}`, M, 24);
      doc.text(`${pIds.length} project${pIds.length !== 1 ? 's' : ''}`, PW - M, 24, { align: 'right' });

      doc.setTextColor(30, 30, 30);
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.text('Project Index', M, 46);
      doc.setDrawColor(...OG); doc.setLineWidth(0.6);
      doc.line(0, 49, PW, 49);

      doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(130, 130, 130);
      doc.text('#', M + 2, 57); doc.text('Project Name', M + 10, 57);
      doc.text('Stage', PW - M - 48, 57); doc.text('Value', PW - M, 57, { align: 'right' });
      doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
      doc.line(0, 59, PW, 59);

      let iy = 67;
      enriched.forEach(({ project }, i) => {
        const val = project.encon_value || project.barbour_value;
        if (iy + 10 > PH - 10) { doc.addPage(); iy = 15; }
        if (i % 2 === 0) { doc.setFillColor(255, 248, 240); doc.rect(0, iy - 7, PW, 10, 'F'); }
        doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...OG);
        doc.text(String(i + 1), M + 2, iy);
        const nm = doc.splitTextToSize(project.name || project.id, PW - M * 2 - 62)[0];
        doc.setTextColor(25, 25, 25); doc.setFont('helvetica', 'normal');
        doc.text(nm, M + 10, iy);
        if (project.stage) { doc.setFontSize(7); doc.setTextColor(100, 100, 100); doc.text(project.stage.substring(0, 20), PW - M - 48, iy, { align: 'right' }); }
        if (val) { doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(40, 40, 40); doc.text(fmt(val), PW - M, iy, { align: 'right' }); }
        doc.link(0, iy - 7, PW, 10, { pageNumber: i + 2 });
        iy += 10;
      });
      doc.setFontSize(6.5); doc.setTextColor(190, 190, 190);
      doc.text('Click any row to jump to that project', M, PH - 8);

      // ── PAGES 2+: one project per page ────────────────────────────────
      for (let idx = 0; idx < enriched.length; idx++) {
        const { project, packages, projectNotes, projectQuotes } = enriched[idx];
        toast.loading(`Building page ${idx + 1} / ${enriched.length}…`, { id: toastId });
        doc.addPage();
        let py = 0;

        // ── Compact title banner ─────────────────────────────────────────
        const titleLines = doc.splitTextToSize(project.name || project.id, PW - M * 2);
        const BANNER_H = 10 + titleLines.length * 6 + 6;
        doc.setFillColor(...OG);
        doc.rect(0, 0, PW, BANNER_H, 'F');
        doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
        doc.text(titleLines, M, 13);
        if (project.barbour_project_name && project.barbour_project_name !== project.name) {
          doc.setFontSize(7); doc.setFont('helvetica', 'italic'); doc.setTextColor(255, 220, 170);
          doc.text(project.barbour_project_name.substring(0, 90), M, BANNER_H - 3);
        }

        // ── Info band ────────────────────────────────────────────────────
        const INFO_H = 20;
        doc.setFillColor(255, 250, 243);
        doc.rect(0, BANNER_H, PW, INFO_H, 'F');
        doc.setDrawColor(...OG); doc.setLineWidth(0.35);
        doc.line(0, BANNER_H + INFO_H, PW, BANNER_H + INFO_H);

        const infoItems = [
          { label: 'Stage',        value: project.stage || '—' },
          { label: 'Value',        value: fmt(project.encon_value || project.barbour_value) },
          { label: 'Location',     value: [project.town, project.postcode].filter(Boolean).join(', ') || '—' },
          { label: 'Closing Date', value: fmtDate(project.closing_date) || '—' },
          { label: 'Barbour ID',   value: project.barbour_project_id || '—' },
        ];
        const iColW = PW / infoItems.length;
        infoItems.forEach(({ label, value }, i) => {
          const ix = i * iColW + M;
          doc.setFontSize(5.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(180, 100, 10);
          doc.text(label.toUpperCase(), ix, BANNER_H + 7);
          doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 30, 30);
          doc.text(String(value).substring(0, 20), ix, BANNER_H + 15);
        });

        py = BANNER_H + INFO_H + 4;

        // ── Helpers (closures over py) ───────────────────────────────────
        const addPageFooter = () => {
          doc.setFontSize(6); doc.setTextColor(180, 180, 180);
          doc.text('↑ Index', M, PH - 5);
          doc.link(M, PH - 9, 14, 6, { pageNumber: 1 });
          doc.text(`${idx + 1} / ${enriched.length}`, PW / 2, PH - 5, { align: 'center' });
          doc.text(TODAY, PW - M, PH - 5, { align: 'right' });
        };
        const ensureSpace = (h) => {
          if (py + h > PH - 14) { addPageFooter(); doc.addPage(); py = 10; }
        };
        const sectionHeading = (title, count, color = OG) => {
          ensureSpace(14);
          doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 30, 30);
          doc.text(count != null ? `${title} (${count})` : title, M, py + 6);
          doc.setDrawColor(...color); doc.setLineWidth(0.5);
          doc.line(0, py + 8, PW, py + 8);
          py += 12;
        };

        // ── Packages ─────────────────────────────────────────────────────
        if (packages.length > 0) {
          sectionHeading('Packages', packages.length);

          for (const pkg of packages) {
            const isSubCon  = pkg._role === 'Sub-Contractor';
            const acctName  = (isSubCon ? pkg.sub_contractor_account_name : pkg.contractor_account_name) || '';
            const pkgStatus = pkg.status || '';
            const pkgVal    = pkg.amount;
            const pkgType   = pkg.package_type || '';
            const pkgCat    = Array.isArray(pkg.package_category) ? pkg.package_category.join(', ') : (pkg.package_category || '');
            const confidence = pkg.confidence != null ? `${pkg.confidence}%` : '';
            const startDate  = fmtDate(pkg.encon_start_date || pkg.closing_date);
            const parentPkg  = isSubCon ? (pkg.parent_contractor_package_name || '') : '';
            const pkgQuotes  = pkg.quotes  || [];
            const pkgNotes   = pkg._notes  || [];
            const pkgTasks   = pkg._tasks  || [];

            // ── Package header ──────────────────────────────────────────
            const BADGE_W  = isSubCon ? 32 : 28;
            const nameMaxW = PW - M - BADGE_W - 6 - M - 44;
            const acctLines = doc.splitTextToSize(acctName || 'Unknown', nameMaxW);
            const HDR_H = Math.max(16, 5 + acctLines.length * 5.5 + 5);

            ensureSpace(HDR_H);
            doc.setFillColor(isSubCon ? 240 : 255, isSubCon ? 233 : 247, isSubCon ? 255 : 235);
            doc.rect(0, py, PW, HDR_H, 'F');

            if (isSubCon) doc.setFillColor(130, 80, 210); else doc.setFillColor(...OG);
            doc.roundedRect(M, py + 3, BADGE_W, 7, 1.5, 1.5, 'F');
            doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
            doc.text(isSubCon ? 'Sub Contractor' : 'Main Contractor', M + 2, py + 8);

            const nameX = M + BADGE_W + 4;
            doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 15, 15);
            doc.text(acctLines, nameX, py + 8);

            if (pkgVal && String(pkgVal) !== '0') {
              doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...OG);
              doc.text(fmt(pkgVal), PW - M, py + 8, { align: 'right' });
            }
            if (pkgStatus) {
              const sc = pkgStatus.toLowerCase().includes('successful') || pkgStatus.toLowerCase().includes('won')
                ? [22,163,74] : pkgStatus.toLowerCase().includes('unsuccessful') || pkgStatus.toLowerCase().includes('lost')
                ? [220,38,38] : [80,80,80];
              doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...sc);
              doc.text(pkgStatus, PW - M, py + 14, { align: 'right' });
            }
            py += HDR_H;

            // ── Package detail row ──────────────────────────────────────
            const detailParts = [
              pkgType   && `Type: ${pkgType}`,
              pkgCat    && `Category: ${pkgCat}`,
              confidence && `Confidence: ${confidence}`,
              startDate  && `Start: ${startDate}`,
              parentPkg  && `Under: ${parentPkg}`,
            ].filter(Boolean);

            if (detailParts.length > 0) {
              const DETAIL_H = Math.ceil(detailParts.length / 2) * 6 + 6;
              ensureSpace(DETAIL_H);
              doc.setFillColor(isSubCon ? 248 : 255, isSubCon ? 244 : 252, isSubCon ? 255 : 250);
              doc.rect(0, py, PW, DETAIL_H, 'F');
              // Lay out in two columns
              const leftCol  = detailParts.filter((_, i) => i % 2 === 0);
              const rightCol = detailParts.filter((_, i) => i % 2 === 1);
              leftCol.forEach((text, i) => {
                doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(90, 90, 90);
                doc.text(text, M + 4, py + 5 + i * 6);
              });
              rightCol.forEach((text, i) => {
                doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(90, 90, 90);
                doc.text(text, PW / 2 + 4, py + 5 + i * 6);
              });
              py += DETAIL_H;
            }

            // ── Contact ──────────────────────────────────────────────────
            if (pkg._firstContact) {
              const contact = pkg._firstContact;
              const contactParts = [
                contact.full_name && `Contact: ${contact.full_name}`,
                (contact.mobile || contact.phone) && `Tel: ${contact.mobile || contact.phone}`,
                contact.email && `Email: ${contact.email}`,
              ].filter(Boolean);
              if (contactParts.length > 0) {
                const CONT_H = 5 + contactParts.length * 5.5;
                ensureSpace(CONT_H);
                doc.setFillColor(240, 248, 255);
                doc.rect(0, py, PW, CONT_H, 'F');
                contactParts.forEach((text, i) => {
                  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 80, 120);
                  doc.text(text, M + 4, py + 4.5 + i * 5.5);
                });
                py += CONT_H;
              }
            }

            // ── Quotes ──────────────────────────────────────────────────
            if (pkgQuotes.length > 0) {
              ensureSpace(10);
              doc.setFillColor(240, 247, 255);
              doc.rect(0, py, PW, 8, 'F');
              doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(60, 100, 180);
              doc.text(`  Quotes (${pkgQuotes.length})`, M, py + 5.5);
              py += 8;

              for (const q of pkgQuotes) {
                const qH = 16;
                ensureSpace(qH);
                doc.setFillColor(252, 254, 255);
                doc.rect(0, py, PW, qH, 'F');
                doc.setDrawColor(210, 225, 255); doc.setLineWidth(0.2);
                doc.line(0, py + qH, PW, py + qH);

                doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
                doc.text(doc.splitTextToSize(q.subject || '—', PW - 2*M - 50)[0], M + 6, py + 6);
                const meta = [q.supplier && `Supplier: ${q.supplier}`, q.description].filter(Boolean).join('  ·  ');
                if (meta) {
                  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(110, 110, 110);
                  doc.text(doc.splitTextToSize(meta, PW - 2*M - 50)[0], M + 6, py + 11.5);
                }
                if (q.stage) { doc.setFontSize(6.5); doc.setTextColor(100,100,100); doc.text(q.stage, PW - M - 28, py + 6, { align: 'right' }); }
                if (q.value) { doc.setFontSize(8); doc.setFont('helvetica','bold'); doc.setTextColor(...OG); doc.text(fmt(q.value), PW - M, py + 6, { align: 'right' }); }
                py += qH;
              }
            }

            // ── Package Notes ────────────────────────────────────────────
            ensureSpace(10);
            doc.setFillColor(255, 253, 240);
            doc.rect(0, py, PW, 8, 'F');
            doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(160, 120, 0);
            doc.text(`  Notes (${pkgNotes.length})`, M, py + 5.5);
            py += 8;

            for (const note of pkgNotes) {
              const contentLines = doc.splitTextToSize(note.content || '', PW - 2*M - 6);
              const visibleLines = contentLines.slice(0, 4);
              const NH = 8 + visibleLines.length * 4.5;
              ensureSpace(NH);
              doc.setFillColor(255, 255, 248);
              doc.rect(0, py, PW, NH, 'F');
              doc.setDrawColor(240, 220, 150); doc.setLineWidth(0.2);
              doc.line(0, py + NH, PW, py + NH);

              if (note.title) {
                doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(100, 70, 0);
                doc.text(note.title, M + 4, py + 5.5);
              }
              doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
              doc.text(visibleLines, M + 4, py + (note.title ? 10 : 5.5));
              if (note.owner_name || note.created_at) {
                doc.setFontSize(5.5); doc.setTextColor(170, 170, 170);
                doc.text(`${note.owner_name || ''}  ${fmtDate(note.created_at)}`.trim(), PW - M, py + NH - 2, { align: 'right' });
              }
              py += NH;
            }

            // ── Package Tasks ────────────────────────────────────────────
            ensureSpace(10);
            doc.setFillColor(240, 255, 245);
            doc.rect(0, py, PW, 8, 'F');
            doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 130, 70);
            doc.text(`  Tasks (${pkgTasks.length})`, M, py + 5.5);
            py += 8;

            for (const task of pkgTasks) {
              const TH = 12;
              ensureSpace(TH);
              doc.setFillColor(248, 255, 251);
              doc.rect(0, py, PW, TH, 'F');
              doc.setDrawColor(180, 230, 200); doc.setLineWidth(0.2);
              doc.line(0, py + TH, PW, py + TH);

              doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
              doc.text(doc.splitTextToSize(task.subject || '—', PW - 2*M - 60)[0], M + 4, py + 5.5);

              const taskMeta = [task.status, task.priority !== 'Normal' && task.priority, task.due_date && `Due: ${fmtDate(task.due_date)}`].filter(Boolean).join('  ·  ');
              if (taskMeta) {
                doc.setFontSize(6); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 100, 100);
                doc.text(taskMeta, PW - M, py + 5.5, { align: 'right' });
              }
              if (task.description) {
                doc.setFontSize(6); doc.setTextColor(120, 120, 120);
                doc.text(doc.splitTextToSize(task.description, PW - 2*M)[0], M + 4, py + 9.5);
              }
              py += TH;
            }

            // Package separator
            doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
            doc.line(0, py + 2, PW, py + 2);
            py += 6;
          }
        } else {
          ensureSpace(12);
          doc.setFontSize(8); doc.setFont('helvetica', 'italic'); doc.setTextColor(160, 160, 160);
          doc.text('No packages assigned to this project', M, py + 6);
          py += 12;
        }

        // ── Project Notes (last note only) ────────────────────────────
        if (projectNotes.length > 0) {
          ensureSpace(14);
          py += 4;
          sectionHeading('Project Notes', null, [200, 160, 0]);

          const note = projectNotes[projectNotes.length - 1];
          const contentLines = doc.splitTextToSize(note.content || '', PW - 2*M - 6);
          const visLines = contentLines.slice(0, 6);
          const NH = 10 + visLines.length * 4.5;
          ensureSpace(NH);
          doc.setFillColor(255, 255, 248);
          doc.rect(0, py, PW, NH, 'F');
          doc.setDrawColor(240, 220, 150); doc.setLineWidth(0.3);
          doc.line(0, py + NH, PW, py + NH);

          if (note.title) {
            doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(100, 70, 0);
            doc.text(note.title, M + 4, py + 6);
          }
          doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
          doc.text(visLines, M + 4, py + (note.title ? 11 : 6));
          if (note.owner_name) {
            doc.setFontSize(5.5); doc.setTextColor(170, 170, 170);
            doc.text(`${note.owner_name}  ${fmtDate(note.created_at)}`.trim(), PW - M, py + NH - 2, { align: 'right' });
          }
          py += NH;
        }

        // ── Project Quotes (not linked to any package) ─────────────────
        if (projectQuotes && projectQuotes.length > 0) {
          ensureSpace(14);
          py += 4;
          sectionHeading('Project Quotes', projectQuotes.length, [60, 100, 180]);

          for (const q of projectQuotes) {
            const qH = 16;
            ensureSpace(qH);
            doc.setFillColor(252, 254, 255);
            doc.rect(0, py, PW, qH, 'F');
            doc.setDrawColor(210, 225, 255); doc.setLineWidth(0.2);
            doc.line(0, py + qH, PW, py + qH);

            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
            doc.text(doc.splitTextToSize(q.subject || '—', PW - 2*M - 50)[0], M + 6, py + 6);
            const qMeta = [q.supplier && `Supplier: ${q.supplier}`, q.description && q.description.substring(0, 80)].filter(Boolean).join('  ·  ');
            if (qMeta) {
              doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(110, 110, 110);
              doc.text(doc.splitTextToSize(qMeta, PW - 2*M - 50)[0], M + 6, py + 11.5);
            }
            if (q.stage) { doc.setFontSize(6.5); doc.setTextColor(100, 100, 100); doc.text(q.stage, PW - M - 28, py + 6, { align: 'right' }); }
            if (q.value) { doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...OG); doc.text(fmt(q.value), PW - M, py + 6, { align: 'right' }); }
            py += qH;
          }
        }

        addPageFooter();
      }

      const filename = `${listName.replace(/\s+/g, '_')}_${TODAY.replace(/\//g, '-')}.pdf`;
      doc.save(filename);
      toast.success(`PDF ready — ${enriched.length} project${enriched.length !== 1 ? 's' : ''}`, { id: toastId });

    } catch (e) {
      toast.error('PDF export failed', { id: toastId });
      console.error('PDF generation error:', e);
    } finally {
      setExporting(false);
    }
  };


  if (projectIds.length === 0) return null;

  const safeIndex = Math.min(activeTabIndex, projectIds.length - 1);
  const activeId = projectIds[safeIndex];
  const activeEntry = projectsData[activeId] || {};

  const tabLabel = (id) => {
    const entry = projectsData[id];
    if (!entry || entry.loading) return '…';
    if (entry.error) return 'Error';
    return truncate(entry.project?.name || '—', 22);
  };

  return (
    <div className="project-tab-view">
      {/* Combined project dropdown + toolbar */}
      <div className="bg-white border-b border-gray-200 shadow-sm print:hidden flex items-center justify-between px-4 py-2.5 gap-2">
        {/* Project selector — dropdown highlighting whichever project is currently being viewed */}
        <div className="relative flex-shrink-0 min-w-0">
          <button
            onClick={() => setProjectDropdownOpen(v => !v)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold text-white transition-all hover:opacity-90 max-w-[280px]"
            style={{ backgroundColor: '#7c3aed' }}
            data-testid="pt-project-dropdown-trigger"
          >
            <FolderKanban className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{tabLabel(activeId)}</span>
            <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${projectDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {projectDropdownOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setProjectDropdownOpen(false)} />
              <div className="absolute top-full left-0 mt-1 w-80 max-h-96 overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                {projectIds.map((id, i) => (
                  <div
                    key={id}
                    className={`flex items-center gap-2 px-3 py-2.5 hover:bg-purple-50 cursor-pointer group border-b border-gray-50 last:border-0 ${i === safeIndex ? 'bg-purple-50' : ''}`}
                  >
                    <button
                      onClick={() => { onTabChange(i); if (!projectsData[id]) fetchProject(id); setProjectDropdownOpen(false); }}
                      className={`flex-1 text-left text-sm truncate ${i === safeIndex ? 'font-bold' : 'font-medium text-gray-700'}`}
                      style={i === safeIndex ? { color: '#7c3aed' } : {}}
                      data-testid={`pt-tab-${i}`}
                    >
                      {tabLabel(id)}
                    </button>
                    <span
                      role="button"
                      onClick={(e) => { e.stopPropagation(); if (window.confirm('Remove this project from the tracking list?')) onRemoveProject(id); }}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all flex-shrink-0"
                      title="Remove from list"
                      data-testid={`pt-tab-remove-${i}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right-side toolbar: list switcher + add + export */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* List switcher */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-black bg-white hover:bg-gray-50 transition-colors text-[12px] font-semibold text-gray-800 max-w-[180px]"
              data-testid="pt-list-switcher"
            >
              <FolderKanban className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ENCON_ORANGE }} />
              <span className="truncate max-w-[110px]">{activeListObj?.name || 'Select list'}</span>
              <ChevronDown className={`w-3 h-3 text-gray-500 ml-auto flex-shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="absolute top-full right-0 mt-1 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                {(lists || []).length > 0 && (
                  <div className="py-1 max-h-60 overflow-y-auto">
                    {(lists || []).map(lst => (
                      <div
                        key={lst.id}
                        className={`flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 cursor-pointer group ${lst.id === activeListId ? 'bg-orange-50' : ''}`}
                      >
                        <button
                          className="flex-1 text-left text-sm font-medium text-gray-800 truncate"
                          onClick={() => { onSelectList(lst.id); setDropdownOpen(false); }}
                          data-testid={`pt-list-option-${lst.id}`}
                        >
                          {lst.name}
                          <span className="ml-2 text-[11px] text-gray-400 font-normal">
                            {(lst.project_ids || []).length} project{(lst.project_ids || []).length !== 1 ? 's' : ''}
                            {' · '}{(lst.branches || []).join(' / ')}
                          </span>
                        </button>
                        {canManageLists && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); onEditList(lst); setDropdownOpen(false); }}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:text-orange-600 transition-all"
                              title="Edit list"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); onDeleteList(lst.id); setDropdownOpen(false); }}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500 transition-all"
                              title="Delete list"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {canManageLists && (
                  <div className="border-t border-gray-100">
                    <button
                      onClick={() => { onNewList(); setDropdownOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold hover:bg-orange-50 transition-colors"
                      style={{ color: ENCON_ORANGE }}
                      data-testid="pt-new-list-btn"
                    >
                      <Plus className="w-4 h-4" />
                      New Tracking List
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add Projects */}
          {canAdd && (
            <button
              onClick={onAddProjects}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-black bg-white text-black text-[12px] font-semibold hover:bg-gray-50 transition-colors"
              data-testid="pt-add-projects-btn"
            >
              <Search className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Add Projects</span>
            </button>
          )}

          {/* Export PDF */}
          <button
            onClick={handleExportPdf}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-black bg-white text-black text-[12px] font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
            data-testid="pt-export-pdf-btn"
            title="Export all projects as PDF"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{exporting ? 'Exporting…' : 'Export PDF'}</span>
          </button>
        </div>
      </div>

      {/* Click-outside to close dropdown */}
      {dropdownOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
      )}

      {/* Active project panel */}
      <div className="flex-1">
        {activeEntry.loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6" style={{ color: '#7c3aed' }} />
          </div>
        ) : activeEntry.error ? (
          <div className="flex items-center justify-center h-64 text-red-400 text-sm">
            Failed to load project. <button onClick={() => { setProjectsData(p => { const n={...p}; delete n[activeId]; return n; }); fetchProject(activeId); }} className="ml-2 underline">Retry</button>
          </div>
        ) : activeEntry.project ? (
          <ProjectDetailPanel
            project={activeEntry.project}
            user={user}
            onAddProjects={onAddProjects}
            onPackagesLoaded={(projectId, pkgs) => {
              setProjectsData(prev => ({ ...prev, [projectId]: { ...prev[projectId], packages: pkgs } }));
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
